import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import { metricsApi, type DailyAgg } from "@/api/metrics";
import { fmt, num } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

function thisMonthIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthBounds(monthIso: string): { from: string; to: string; days: string[] } {
  const [year, month] = monthIso.split("-").map(Number);
  const first = new Date(Date.UTC(year, month - 1, 1));
  const last = new Date(Date.UTC(year, month, 0));
  const days: string[] = [];
  for (let d = 1; d <= last.getUTCDate(); d++) {
    days.push(`${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
  }
  return {
    from: first.toISOString().slice(0, 10),
    to: last.toISOString().slice(0, 10),
    days,
  };
}

interface DerivedMetrics {
  // Прямі поля + обчислені.
  fact_revenue: number;
  plan_revenue: number;
  rev_pct: number | null;
  fact_profit: number;
  plan_profit: number;
  prof_pct: number | null;
  fact_kwh: number;
  fact_heat_gcal: number;
  fact_heat_revenue: number;
  imbalance_payment: number;
  balance_violations: number;
  alarm_hours: number;
  fact_cost: number;
  fact_per_mwh: number;
  avg_power_kw: number;
}

function derive(r: DailyAgg | null): DerivedMetrics {
  const fact_revenue = num(r?.fact_revenue_uah) ?? 0;
  const plan_revenue = num(r?.plan_revenue_uah) ?? 0;
  const fact_profit = num(r?.fact_profit_uah) ?? 0;
  const plan_profit = num(r?.plan_profit_uah) ?? 0;
  const fact_kwh = num(r?.fact_generation_kwh) ?? 0;
  const fact_cost = fact_revenue - fact_profit;
  return {
    fact_revenue,
    plan_revenue,
    rev_pct: plan_revenue > 0 ? (fact_revenue / plan_revenue) * 100 : null,
    fact_profit,
    plan_profit,
    prof_pct: plan_profit > 0 ? (fact_profit / plan_profit) * 100 : null,
    fact_kwh,
    fact_heat_gcal: num(r?.fact_heat_gcal) ?? 0,
    fact_heat_revenue: num(r?.fact_heat_revenue_uah) ?? 0,
    imbalance_payment: num(r?.imbalance_payment_uah) ?? 0,
    balance_violations: r?.balance_violations ?? 0,
    alarm_hours: r?.alarm_hours ?? 0,
    fact_cost,
    fact_per_mwh: fact_kwh > 0 ? fact_cost / (fact_kwh / 1000) : 0,
    avg_power_kw: fact_kwh / 24,
  };
}

function pctColor(pct: number | null): string {
  if (pct === null) return "";
  if (pct >= 95 && pct <= 105) return "text-emerald-700";
  if (pct >= 85 && pct < 95) return "text-amber-700";
  if (pct > 105 && pct <= 115) return "text-amber-700";
  return "text-red-700";
}

export function MonthlyPage() {
  const { t } = useTranslation();
  const [monthIso, setMonthIso] = useState(thisMonthIso());
  const { from, to, days } = useMemo(() => monthBounds(monthIso), [monthIso]);

  const daily = useQuery({
    queryKey: ["metrics-daily", from, to],
    queryFn: () => metricsApi.daily(from, to),
  });

  const byDay = useMemo(() => {
    const m = new Map<string, DailyAgg>();
    (daily.data ?? []).forEach((r) => m.set(r.day.slice(0, 10), r));
    return m;
  }, [daily.data]);

  const rows = useMemo(
    () => days.map((d) => ({ day: d, raw: byDay.get(d) ?? null, m: derive(byDay.get(d) ?? null) })),
    [days, byDay],
  );

  const totals = useMemo(() => {
    const t: DerivedMetrics = {
      fact_revenue: 0,
      plan_revenue: 0,
      rev_pct: null,
      fact_profit: 0,
      plan_profit: 0,
      prof_pct: null,
      fact_kwh: 0,
      fact_heat_gcal: 0,
      fact_heat_revenue: 0,
      imbalance_payment: 0,
      balance_violations: 0,
      alarm_hours: 0,
      fact_cost: 0,
      fact_per_mwh: 0,
      avg_power_kw: 0,
    };
    let dayCount = 0;
    for (const { m } of rows) {
      if (!m) continue;
      t.fact_revenue += m.fact_revenue;
      t.plan_revenue += m.plan_revenue;
      t.fact_profit += m.fact_profit;
      t.plan_profit += m.plan_profit;
      t.fact_kwh += m.fact_kwh;
      t.fact_heat_gcal += m.fact_heat_gcal;
      t.fact_heat_revenue += m.fact_heat_revenue;
      t.imbalance_payment += m.imbalance_payment;
      t.balance_violations += m.balance_violations;
      t.alarm_hours += m.alarm_hours;
      t.fact_cost += m.fact_cost;
      if (m.fact_kwh > 0) dayCount++;
    }
    t.rev_pct = t.plan_revenue > 0 ? (t.fact_revenue / t.plan_revenue) * 100 : null;
    t.prof_pct = t.plan_profit > 0 ? (t.fact_profit / t.plan_profit) * 100 : null;
    t.fact_per_mwh = t.fact_kwh > 0 ? t.fact_cost / (t.fact_kwh / 1000) : 0;
    t.avg_power_kw = dayCount > 0 ? t.fact_kwh / (dayCount * 24) : 0;
    return t;
  }, [rows]);

  // Heatmap intensity: profit per day relative to max abs profit.
  const maxAbsProfit = useMemo(() => {
    return Math.max(0.01, ...rows.map((r) => Math.abs(r.m.fact_profit)));
  }, [rows]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("monthly.title")}</CardTitle>
          <CardDescription>{t("monthly.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 w-fit">
            <Label>{t("monthly.month")}</Label>
            <Input
              type="month"
              value={monthIso}
              onChange={(e) => setMonthIso(e.target.value)}
              className="w-[180px]"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("monthly.heatmap_title")}</CardTitle>
          <CardDescription>{t("monthly.heatmap_subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-1 max-w-[420px]">
            {rows.map(({ day, m }) => {
              const dayNum = Number(day.slice(8, 10));
              const intensity = m.fact_profit / maxAbsProfit;
              const bg =
                m.fact_profit > 0
                  ? `rgba(16, 185, 129, ${Math.max(0.05, intensity).toFixed(2)})`
                  : m.fact_profit < 0
                    ? `rgba(239, 68, 68, ${Math.max(0.05, Math.abs(intensity)).toFixed(2)})`
                    : "rgb(241, 245, 249)";
              return (
                <div
                  key={day}
                  title={`${day}: ${fmt(m.fact_profit)} грн`}
                  className="aspect-square rounded text-xs flex items-center justify-center font-mono"
                  style={{ backgroundColor: bg }}
                >
                  {dayNum}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {daily.isLoading ? (
            <div className="p-6 text-sm text-slate-500">{t("common.loading")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-slate-500 text-xs">
                    <th className="p-2">{t("monthly.day")}</th>
                    <th className="p-2 text-right">{t("monthly.fact_kwh")}</th>
                    <th className="p-2 text-right">{t("monthly.avg_power")}</th>
                    <th className="p-2 text-right">{t("monthly.plan_revenue")}</th>
                    <th className="p-2 text-right">{t("monthly.fact_revenue")}</th>
                    <th className="p-2 text-right">{t("monthly.rev_pct")}</th>
                    <th className="p-2 text-right">{t("monthly.plan_profit")}</th>
                    <th className="p-2 text-right">{t("monthly.fact_profit")}</th>
                    <th className="p-2 text-right">{t("monthly.prof_pct")}</th>
                    <th className="p-2 text-right">{t("monthly.fact_cost")}</th>
                    <th className="p-2 text-right">{t("monthly.cost_per_mwh")}</th>
                    <th className="p-2 text-right">{t("monthly.imbalance")}</th>
                    <th className="p-2 text-right">{t("monthly.heat_gcal")}</th>
                    <th className="p-2 text-right">{t("monthly.heat_revenue")}</th>
                    <th className="p-2 text-right">{t("monthly.balance_violations")}</th>
                    <th className="p-2 text-right">{t("monthly.alarm_hours")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ day, m }) => (
                    <tr key={day} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="p-2 font-mono text-xs">{day.slice(8, 10)}</td>
                      <td className="p-2 text-right font-mono">{fmt(m.fact_kwh)}</td>
                      <td className="p-2 text-right font-mono">{fmt(m.avg_power_kw)}</td>
                      <td className="p-2 text-right font-mono text-slate-500">{fmt(m.plan_revenue)}</td>
                      <td className="p-2 text-right font-mono">{fmt(m.fact_revenue)}</td>
                      <td className={cn("p-2 text-right font-mono", pctColor(m.rev_pct))}>
                        {m.rev_pct === null ? "—" : `${fmt(m.rev_pct, 1)}%`}
                      </td>
                      <td className="p-2 text-right font-mono text-slate-500">{fmt(m.plan_profit)}</td>
                      <td
                        className={cn(
                          "p-2 text-right font-mono",
                          m.fact_profit < 0 ? "text-red-700" : "",
                        )}
                      >
                        {fmt(m.fact_profit)}
                      </td>
                      <td className={cn("p-2 text-right font-mono", pctColor(m.prof_pct))}>
                        {m.prof_pct === null ? "—" : `${fmt(m.prof_pct, 1)}%`}
                      </td>
                      <td className="p-2 text-right font-mono">{fmt(m.fact_cost)}</td>
                      <td className="p-2 text-right font-mono">{fmt(m.fact_per_mwh)}</td>
                      <td className="p-2 text-right font-mono">{fmt(m.imbalance_payment)}</td>
                      <td className="p-2 text-right font-mono">{fmt(m.fact_heat_gcal, 2)}</td>
                      <td className="p-2 text-right font-mono">{fmt(m.fact_heat_revenue)}</td>
                      <td
                        className={cn(
                          "p-2 text-right font-mono",
                          m.balance_violations > 0 ? "text-red-700 font-semibold" : "text-slate-400",
                        )}
                      >
                        {m.balance_violations}
                      </td>
                      <td
                        className={cn(
                          "p-2 text-right font-mono",
                          m.alarm_hours > 0 ? "text-red-700 font-semibold" : "text-slate-400",
                        )}
                      >
                        {m.alarm_hours}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 font-semibold">
                  <tr>
                    <td className="p-2">{t("common.total")}</td>
                    <td className="p-2 text-right font-mono">{fmt(totals.fact_kwh)}</td>
                    <td className="p-2 text-right font-mono">{fmt(totals.avg_power_kw)}</td>
                    <td className="p-2 text-right font-mono text-slate-500">{fmt(totals.plan_revenue)}</td>
                    <td className="p-2 text-right font-mono">{fmt(totals.fact_revenue)}</td>
                    <td className={cn("p-2 text-right font-mono", pctColor(totals.rev_pct))}>
                      {totals.rev_pct === null ? "—" : `${fmt(totals.rev_pct, 1)}%`}
                    </td>
                    <td className="p-2 text-right font-mono text-slate-500">{fmt(totals.plan_profit)}</td>
                    <td
                      className={cn(
                        "p-2 text-right font-mono",
                        totals.fact_profit < 0 ? "text-red-700" : "",
                      )}
                    >
                      {fmt(totals.fact_profit)}
                    </td>
                    <td className={cn("p-2 text-right font-mono", pctColor(totals.prof_pct))}>
                      {totals.prof_pct === null ? "—" : `${fmt(totals.prof_pct, 1)}%`}
                    </td>
                    <td className="p-2 text-right font-mono">{fmt(totals.fact_cost)}</td>
                    <td className="p-2 text-right font-mono">{fmt(totals.fact_per_mwh)}</td>
                    <td className="p-2 text-right font-mono">{fmt(totals.imbalance_payment)}</td>
                    <td className="p-2 text-right font-mono">{fmt(totals.fact_heat_gcal, 2)}</td>
                    <td className="p-2 text-right font-mono">{fmt(totals.fact_heat_revenue)}</td>
                    <td className="p-2 text-right font-mono">{totals.balance_violations}</td>
                    <td className="p-2 text-right font-mono">{totals.alarm_hours}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
